import { Component, computed, signal } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from 'src/app/infrastructure/services/api-service/api.service';
import { AppDatePipe } from 'src/app/infrastructure/pipes/app-date.pipe';
import { FilterService } from 'src/app/features/shared/filter/filter.service';
import { FunctionMemberListService } from '../../services/function-member-list.service';
import { FunctionMemberPageService } from '../../services/function-member-page.service';
import { functionMemberListData, IFunctionMemberListData } from '../../models/function-member-list-data';
import { functionMemberClickTarget, functionMemberListView_default, functionMemberListViewSpec, functionMemberPanel, functionMemberPhotoSource, functionMemberRowPart, TFunctionMemberRowPart } from '../../models/function-member-list-view';
import { listViewSpec_get } from 'src/app/features/shared/central-page/models/list-view';
import { IFunctionMemberListDto } from 'src/assets/common/models/function-member-list-dto.g';
import { ImageBoxTabListComponent } from 'src/app/features/shared/image-boxes/image-box-tab-list/image-box-tab-list.component';
import { InactiveSuffixPipe, inactiveSuffixText } from 'src/app/infrastructure/pipes/inactive-suffix.pipe';
import { LIST_KIND_NAME, _ListService } from 'src/app/infrastructure/services/list-services/_list.service';
import { ExpansionList_ToolComponent } from 'src/app/features/shared/lists/expansion-list/container/expansion-list-tool/expansion-list.component';
import { PagerService } from 'src/app/features/shared/pager/pager.service';
import { SharedModule } from 'src/app/features/shared/shared.module';
import { TabDataService } from 'src/app/features/shared/central-page/services/tab-data.service';
import { TabListService } from 'src/app/features/shared/central-page/services/tab-list.service';
import { _NavigationList_Base_FeatureComponent } from 'src/app/infrastructure/components/tablist-components/_navigation-list-base-feature.component';
import { _NavigationList_Base_ToolComponent } from 'src/app/infrastructure/components/tablist-components/_navigation-list-base-tool.component';
import { _PageService } from 'src/app/infrastructure/services/page-services/_page.service';
import { claimPage } from 'src/app/infrastructure/models/claim-page';
import { functionMemberData } from '../../models/function-member-data';
import { functionMemberPeriodEndReasonLabel, IFunctionMemberPeriod } from '../../models/function-member-period';
import { httpParamKey } from 'src/app/infrastructure/services/request-services/models/http-param-name';
import { requestName } from 'src/assets/common/enums/request-names.g';
import { tabName } from 'src/app/infrastructure/services/page-services/models/tab-name';
import { TabListStorageService } from 'src/app/features/shared/storage/services/storage-services/tab-list-storage.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { ILevelGeneralListData } from 'src/app/infrastructure/models/level-general-list-data';
import { ApplicationUserService } from 'src/app/infrastructure/services/application-user.services/application-user.service';
import { domainData } from 'src/app/features/domain/models/domain-data';
import { domainUserData } from 'src/app/features/domain-user/models/domain-user-data';
import { imageBoxData } from 'src/app/features/image/models/image-box-data';
import { imageData } from 'src/app/features/image/models/image-data';

@Component({
    selector: 'app-function-member-tablist',
    templateUrl: './function-member-tablist.component.html',
    styleUrls: [
        '../../../shared/central-page/central-page.scss',
        '../../../shared/central-page/container/tablist/tablist.scss',
        '../../../domain-user/components/domain-user-row.scss',
        './function-member-tablist.component.scss',
    ],
    standalone: true,
    imports: [
        ExpansionList_ToolComponent,
        AppDatePipe,
        InactiveSuffixPipe,
        SharedModule,
        ImageBoxTabListComponent,
    ],
    providers: [
        {provide: LIST_KIND_NAME, useValue: tabName.functionMembers},
        TabListStorageService,
        TabDataService,
        PagerService,
        {provide: FilterService, useExisting: PagerService},

        FunctionMemberPageService,
        {provide: _PageService, useExisting: FunctionMemberPageService},
        {provide: _ListService, useExisting: FunctionMemberListService},
    ]
})
export class FunctionMemberTablistComponent
    extends _NavigationList_Base_FeatureComponent<IFunctionMemberListDto, IFunctionMemberListData>
{
    private readonly __apiService = injectRequired(ApiService);
    private readonly __fmPageService = injectRequired(FunctionMemberPageService);
    private readonly __applicationUserService = injectRequired(ApplicationUserService);

    protected override readonly _listService = injectRequired(_ListService);
    protected override readonly _pageService = injectRequired(_PageService);
    protected override readonly _pagerService = injectRequired(PagerService);
    protected override readonly _tabDataService = injectRequired(TabDataService);
    protected override readonly _tabListService = injectRequired(TabListService);

    override input_btnAddData$$ = this._input_btnAddData$$;
    override input_btnDeleteData$$ = this._input_btnDeleteData$$;
    override input_buttonsHide$$ = this._input_buttonsHide$$;
    override input_claimPageList$$ = this._input_claimPageList$$;
    override input_noClick$$ = this._input_noClick$$;
    override input_pageSize$$ = this._input_pageSize$$;

    override out_onClick = this._out_onClick;
    override out_onPageLoaded = this._out_onPageLoaded;
    override out_onSave = this._out_onSave;

    // Functies worden alleen via de members-sync-checklists aan- en uitgezet (plan A): nooit een add-knop.
    override btnAddData$$ = computed(() => ({...(this._input_btnAddData$$() ?? {}), hide: true}));
    override btnDeleteData$$ = this._input_btnDeleteData$$;
    override buttonsHide$$ = this._input_buttonsHide$$;
    override httpParams$$ = this._computed_httpParams$$;
    override list$$ = this._computed_list$$;
    override noClick$$ = this._input_noClick$$;
    override pageSize$$ = this._input_pageSize$$;
    override requestName$$ = this._computed_requestName$$;
    override tabIndex$$ = this._computed_tabIndex$$;

    override claimPageList$$ = signal(claimPage.functionMembers);
    // Zelfde rij-opmaak als de DomainUsers-tab (domain-user-row.scss), met het rol-icoon naast de
    // tekst i.p.v. als badge op de foto. Het aantal regels volgt uit de weergave-versie (ngOnInit).
    override itemLines = 3;
    override hasImageBox = true;

    // Weergave-versie uit de tab-configuratie van de pagina (TabDataTemplate.listView) — zie
    // function-member-list-view.ts. Onbekend/ontbrekend: standaardversie + waarschuwing.
    readonly viewSpec$$ = computed(() => listViewSpec_get(functionMemberListViewSpec, this._computed_listView$$(), functionMemberListView_default, 'FunctionMembers'));
    protected readonly _rowPart = functionMemberRowPart;
    protected readonly _photoNone = functionMemberPhotoSource.none;

    // Lege regel overslaan: "project: …" alleen bij een project-functie.
    partVisible(item: IFunctionMemberListData, part: TFunctionMemberRowPart): boolean {
        return part !== functionMemberRowPart.project || !!item?.[functionMemberListData.project_name];
    }

    function_isAdmin = functionMemberListData.function_isAdmin;
    function_isProjectManager = functionMemberListData.function_isProjectManager;
    function_label = functionMemberListData.function_label;

    photoGuid(item: IFunctionMemberListData): string | null {
        const __photo = this.viewSpec$$().photo;
        if (__photo === functionMemberPhotoSource.person) return item[functionMemberListData.user_photo_guid];
        // Home: de rij ís je eigen lidmaatschap, dus de persoonlijke domeinfoto komt uit de rij zelf.
        if (__photo === functionMemberPhotoSource.membership) return (!item[functionMemberListData.project_guid] ? item[functionMemberListData.domainUser_photo_guid] : null)
            ?? item[functionMemberListData.context_photo_guid];
        return this.__ownDomainPhoto(item)?.[imageBoxData.photoThumbData]?.[imageData.guid]
            ?? item[functionMemberListData.context_photo_guid];
    }

    photoVersion(item: IFunctionMemberListData): string | null {
        const __photo = this.viewSpec$$().photo;
        if (__photo === functionMemberPhotoSource.person) return item[functionMemberListData.user_photo_version];
        if (__photo === functionMemberPhotoSource.membership) return (!item[functionMemberListData.project_guid] && item[functionMemberListData.domainUser_photo_guid]
            ? item[functionMemberListData.domainUser_photo_version] : item[functionMemberListData.context_photo_version]) ?? null;
        const __own = this.__ownDomainPhoto(item);
        if (__own) return __own[imageBoxData.photoThumbData]?.[imageData.modifiedOn] ?? __own[imageBoxData.photoData]?.[imageData.modifiedOn] ?? null;
        return item[functionMemberListData.context_photo_version];
    }

    // Domain-functie: de persoonlijke domeinfoto van de kijker (DomainUser.DomainFileBoxId) gaat voor
    // de hoofdfoto van het domain (context_photo, de fallback) — zelfde regel als de Domain-banner.
    // Alleen als het lidmaatschap van de kijker bij hetzelfde domain hoort als de functie.
    private __ownDomainPhoto(item: IFunctionMemberListData) {
        if (item[functionMemberListData.project_guid]) return null;
        const __viewer = this.__applicationUserService.domainUserData;
        if (__viewer?.[domainUserData.domainData]?.[domainData.guid] !== item[functionMemberListData.domain_guid]) return null;
        const __box = __viewer?.[domainUserData.domainPhotoData] ?? null;
        return __box?.[imageBoxData.photoThumbData]?.[imageData.guid] ? __box : null;
    }
    project_name = functionMemberListData.project_name;
    displayName = functionMemberListData.displayName;
    user_email = functionMemberListData.user_email;
    organisationLabel = functionMemberListData.organisation_label;
    // Identificatie "naam · organisatie" (organisationLabel), voor iedereen in het domain gelijk.
    readonly organisationSeparator = ' · ';
    domain_name = functionMemberListData.domain_name;
    startDate = functionMemberListData.startDate;
    endDate = functionMemberListData.effectiveEndDate;
    active = functionMemberListData.active;

    override ngOnInit(): void {
        // Vóór de eerste render: de lijst-tool leest itemLines eenmalig bij zijn eigen init.
        this.itemLines = this.viewSpec$$().lines.length;
        // Zonder foto (photo: none) ook geen foto-rijhoogte (ItemSize).
        this.hasImageBox = this.viewSpec$$().photo !== functionMemberPhotoSource.none;
        this._ngOnInit();
    }

    override emit_btnAddClick(): void {}

    // Uitklapbare historie (plan A) in een ExpansionListComponent: de rij is de functie (met de laatste
    // periode), het paneel toont de eerdere periodes. Alleen uitklapbaar als die er zijn; de knop toont
    // het aantal. Een klik op de rij zelf blijft doorklikken (weergave-versie, zie emit_onClick).
    earlierPeriods(item: IFunctionMemberListData): IFunctionMemberPeriod[] {
        return (item?.[functionMemberListData.periods] ?? []).slice(1);
    }
    allPeriods(item: IFunctionMemberListData): IFunctionMemberPeriod[] {
        return item?.[functionMemberListData.periods] ?? [];
    }
    // Paneel 'details': de volledige historie, uitklapbaar zodra er periodes zijn, knop zonder tekst.
    // Paneel 'periods': alleen bij eerdere periodes, knop met het aantal. Paneel 'none': nooit uitklapbaar.
    readonly expandableOf = (item: ILevelGeneralListData): boolean =>
        this.viewSpec$$().panel === functionMemberPanel.details
            ? this.allPeriods(item as IFunctionMemberListData).length > 0
            : this.viewSpec$$().panel !== functionMemberPanel.none
                && this.earlierPeriods(item as IFunctionMemberListData).length > 0;
    readonly toggleTextOf = (item: ILevelGeneralListData): string | null =>
        this.viewSpec$$().panel === functionMemberPanel.details
            ? null
            : String(this.earlierPeriods(item as IFunctionMemberListData).length);

    // "naam (e-mail)" in het vaste format van de DomainUser-lijsten (emailSuffix + .email-suffix), de
    // inactief-marker achter het geheel.
    protected readonly _inactiveSuffixText = inactiveSuffixText;
    protected readonly _panel = functionMemberPanel;
    protected readonly _click = functionMemberClickTarget;
    protected readonly _periodReasonLabel = functionMemberPeriodEndReasonLabel;
    protected readonly _periodText = {
        from: 'van',
        until: 't/m',
        running: 'lopend',
        fullDays: 'volle dagen',
    };

    // Waar een klik naartoe gaat volgt uit de weergave-versie (functionMemberClickTarget).
    override emit_onClick(item: ILevelGeneralListData): void {
        const __item = item as IFunctionMemberListData;
        switch (this.viewSpec$$().click) {
            case functionMemberClickTarget.functionMember:
                this.__fmPageService.projectPageRoute_goto(__item);
                return;
            case functionMemberClickTarget.function:
                this.__fmPageService.functionPageForItem_goto(__item);
                return;
            case functionMemberClickTarget.none:
                // Geen doorklik: de rij klapt het paneel open (ExpansionListComponent rowToggles).
                return;
            case functionMemberClickTarget.member:
                this.__fmPageService.memberRoute_goto(__item);
                return;
        }
    }

    override emit_onRestore(restoreList: any[]): void {
        this._restore$(restoreList, requestName.listRestore).subscribe();
    }

    protected override _list_get$(): Observable<IFunctionMemberListDto[]> {
        // AdminPanel geeft geen dauGuid in httpParams mee (die zit — gefreezed op domain 0 — alleen in
        // de route-keys, zie adminPanelModelResolver).
        const __dauGuid = this.httpParams$$()?.get(httpParamKey.domainAUserGuid) || this._activatedRouteService.routeKeys?.dauGuid || undefined;
        const __pamGuid = this.httpParams$$()?.get(httpParamKey.projectAMemberGuid) || undefined;
        // De DomainUser/ProjectMember (of Function) van de geopende pagina — niet de bezoeker zelf.
        const __key = this.httpParams$$()?.get(httpParamKey.key) || undefined;
        const __rn = this.requestName$$();
        if (__rn === requestName.listFromUser) {
            return from(this.__apiService.GET('/api/FunctionMember/list-from-user', {
                params: { query: {} }
            })).pipe(map(r => (r.data as any)?.list as IFunctionMemberListDto[] ?? []));
        }
        if (__rn === requestName.listFromFunction) {
            return from(this.__apiService.GET('/api/FunctionMember/list-from-function', {
                params: { query: { key: __key ?? '', domainAUserGuid: __dauGuid, projectAMemberGuid: __pamGuid } }
            })).pipe(map(r => (r.data as any)?.list as IFunctionMemberListDto[] ?? []));
        }
        // Project.FunctionMembers — zie FunctionMemberController.ListFromProject.
        if (__rn === requestName.listFromProject) {
            return from(this.__apiService.GET('/api/FunctionMember/list-from-project', {
                params: { query: { domainAUserGuid: __dauGuid, projectAMemberGuid: __pamGuid } }
            })).pipe(map(r => (r.data as any)?.list as IFunctionMemberListDto[] ?? []));
        }
        // Combinatie-lijst (domain- én project-scoped) voor de Functions-tab op de DomainUser-pagina
        // — zie FunctionMemberController.ListFromDomainUser.
        if (__rn === requestName.listFromDomainUser) {
            return from(this.__apiService.GET('/api/FunctionMember/list-from-domain-user', {
                params: { query: { key: __key, domainAUserGuid: __dauGuid } }
            })).pipe(map(r => (r.data as any)?.list as IFunctionMemberListDto[] ?? []));
        }
        // Zelfde soort combinatie-lijst, maar dan voor de Functions-tab op de ProjectMember-pagina —
        // zie FunctionMemberController.ListFromProjectMember.
        if (__rn === requestName.listFromProjectMember) {
            return from(this.__apiService.GET('/api/FunctionMember/list-from-project-member', {
                params: { query: { key: __key, domainAUserGuid: __dauGuid, projectAMemberGuid: __pamGuid } }
            })).pipe(map(r => (r.data as any)?.list as IFunctionMemberListDto[] ?? []));
        }
        return from(this.__apiService.GET('/api/FunctionMember/list-from-domain', {
            params: { query: { domainAUserGuid: __dauGuid } }
        })).pipe(map(r => (r.data as any)?.list as IFunctionMemberListDto[] ?? []));
    }

    protected override _restore_patch$(restoreList: any[], httpParams: HttpParams | null): Observable<IFunctionMemberListDto> {
        throw new Error('Method not implemented.');
    }

}
